import { useEffect, useRef, useState } from "react";
import lottie, { type AnimationItem } from "lottie-web";
import { useTheme } from "next-themes";

// Two separate animation files for proper theme support
import splashAnimationLight from "@/assets/splash-animation.json";
import splashAnimationDark from "@/assets/splash-animation-dark.json";

interface LottieLoaderProps {
  size?: number;
  className?: string;
  loop?: boolean;
  message?: string;
}

/**
 * Animated Lottie loader with theme-aware colors.
 * Uses separate animation files for each theme:
 * - Light theme: Dark logo (splash-animation.json)
 * - Dark theme: White logo (splash-animation-dark.json)
 */
export function LottieLoader({ 
  size = 64, 
  className = "",
  loop = true,
  message,
}: LottieLoaderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);
  const { theme, resolvedTheme } = useTheme();
  
  // Don't render animation until mounted to prevent hydration mismatch
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // Determine if we're in dark mode
  const isDark = resolvedTheme === "dark" || theme === "dark";
  
  // Select the appropriate animation data based on theme
  const animationData = isDark ? splashAnimationDark : splashAnimationLight;

  useEffect(() => {
    // Only create animation when mounted (prevents hydration issues)
    if (!containerRef.current || !mounted) return;
    
    // Destroy previous animation if it exists
    if (animationRef.current) {
      animationRef.current.destroy();
    }
    
    // Create new animation with theme-appropriate data
    animationRef.current = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop,
      autoplay: true,
      animationData,
    });
    
    return () => {
      if (animationRef.current) {
        animationRef.current.destroy();
        animationRef.current = null;
      }
    };
  }, [animationData, loop, mounted]);

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      {mounted ? (
        <div 
          ref={containerRef} 
          style={{ width: size, height: size }}
        />
      ) : (
        <div style={{ width: size, height: size }} />
      )}
      {message && (
        <p className="mt-2.5 text-xs text-muted-foreground">{message}</p>
      )}
    </div>
  );
}
